import React from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import {
  RedirectInvestorsToClients,
  RedirectInvestorProfileToClient,
  RedirectInvestmentDetailInvestorSegment,
  RedirectWithdrawalDetailInvestorSegment,
} from './lib/routes/legacyInvestorRedirects'
import {
  LoginContainer,
  ForgotPasswordContainer,
  ResetInstructionsContainer,
  ResetPasswordContainer,
  OpportunityFormContainer,
  InvestorsListContainer,
  ProfileDetailsContainer,
  InvestmentsUserListContainer,
  InvestmentOpportunityListContainer,
  InvestmentDetailContainer,
  QueriesList,
  BusinessInquiriesList,
  OrdersContainer,
  WithdrawalsUserListContainer,
  WithdrawalDetailContainer,
  WithdrawalOpportunityListContainer,
  UnitPriceFormContainer,
  InterestPayoutFormContainer,
  DividendPayoutFormContainer,
  OpportunityQuestionsContainer,
  OpportunityQuestionReplyContainer,
  SecondaryMarketOrdersContainer,
  WalletTopupsContainer,
  WalletWithdrawalsContainer,
  TradingAdminContainer,
  AllocationRequestsListContainer,
  AllocationRequestDetailContainer,
  InvestmentLimitRequestsListContainer,
  InvestmentLimitRequestDetailContainer,
} from './pages'
import { Navbar, Footer, Banner } from './lib/components'
import { PublicRoute, PrivateRoute } from './route-permissions'
import { OpportunityListContainer } from './pages/opportunities/opportunity-list-container'
import MaintenancePage from './lib/components/MaintenancePage'
import './App.scss'
import { BlogListContainer } from './pages/blogs/blog-list-container'
import { BlogsFormContainer } from './pages/blogs-form/blogs-form-container'
import { WebinarListContainer } from './pages/webinars/webinar-list-container'
import { WebinarsFormContainer } from './pages/webinars-form/webinars-form-container'
import { NewsletterGroupsListContainer } from './pages/newsletter/newsletter-groups-list-container'
import { NewsletterGroupFormContainer } from './pages/newsletter-group-form/newsletter-group-form-container'
import { NewsletterSendFormContainer } from './pages/newsletter-form/newsletter-send-form-container'
import { NewsletterSubscribersListContainer } from './pages/newsletter/newsletter-subscribers-list-container'
import { NewsScheduleFormContainer } from './pages/news-schedule-form/news-schedule-form-container'
import { OpportunityNewsListContainer } from './pages/opportunity-news-list/opportunity-news-list-container'
import { CampaignFormContainer } from './pages/campaign-form/campaign-form-container'
import { CampaignsListContainer } from './pages/campaigns-list/campaigns-list-container'
import { ComingSoonListContainer } from './pages/coming-soon-list/coming-soon-list-container'
import { ComingSoonFormContainer } from './pages/coming-soon-form/coming-soon-form-container'
import { AdminTeamContainer } from './pages/admin-team/admin-team-container'
import { AccountChangePasswordContainer } from './pages/account-change-password/account-change-password-container'
import { FaqListContainer } from './pages/faq-list/faq-list-container'
import { FaqFormContainer } from './pages/faq-form/faq-form-container'

const App = () => {
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true'
  if (isMaintenanceMode) {
    return <MaintenancePage />
  }

  return (
    <BrowserRouter>
      <Navbar />
      <Banner />
      <div className='App'>
        <Routes>
          {['/', '/login'].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <PublicRoute>
                  <LoginContainer />
                </PublicRoute>
              }
            />
          ))}
          <Route
            path='forgot-password'
            element={
              <PublicRoute>
                <ForgotPasswordContainer />
              </PublicRoute>
            }
          />
          <Route
            path='reset-instructions'
            element={
              <PublicRoute>
                <ResetInstructionsContainer />
              </PublicRoute>
            }
          />
          <Route
            path='reset-password'
            element={
              <PublicRoute>
                <ResetPasswordContainer />{' '}
              </PublicRoute>
            }
          />
          <Route
            path='opportunities/new'
            element={
              <PrivateRoute requiredFeature={{ feature: 'opportunities', minLevel: 'write' }}>
                <OpportunityFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='/opportunities/:projectSlug/:id/edit'
            element={
              <PrivateRoute requiredFeature={{ feature: 'opportunities', minLevel: 'write' }}>
                <OpportunityFormContainer />
              </PrivateRoute>
            }
          />
          <Route path='investors' element={<RedirectInvestorsToClients />} />
          <Route
            path='clients'
            element={
              <PrivateRoute requiredFeature={{ feature: 'investors', minLevel: 'read' }}>
                <InvestorsListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='investor/:investorId/profile'
            element={<RedirectInvestorProfileToClient />}
          />
          <Route
            path='client/:investorId/profile'
            element={
              <PrivateRoute requiredFeature={{ feature: 'investors', minLevel: 'read' }}>
                <ProfileDetailsContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='opportunity/investments/:projectSlug/:projectId'
            element={
              <PrivateRoute requiredFeature={{ feature: 'investments', minLevel: 'read' }}>
                <InvestmentsUserListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='opportunity/:projectSlug/:projectId/investor/:investorId/investment/detail/:investmentId'
            element={<RedirectInvestmentDetailInvestorSegment />}
          />
          <Route
            path='opportunity/:projectSlug/:projectId/client/:investorId/investment/detail/:investmentId'
            element={
              <PrivateRoute requiredFeature={{ feature: 'investments', minLevel: 'read' }}>
                <InvestmentDetailContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='opportunity/withdrawals/:projectSlug/:projectId'
            element={
              <PrivateRoute requiredFeature={{ feature: 'withdrawals', minLevel: 'read' }}>
                <WithdrawalsUserListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='opportunity/:projectSlug/:projectId/investor/:investorId/withdrawal/detail/:withdrawalId'
            element={<RedirectWithdrawalDetailInvestorSegment />}
          />
          <Route
            path='opportunity/:projectSlug/:projectId/client/:investorId/withdrawal/detail/:withdrawalId'
            element={
              <PrivateRoute requiredFeature={{ feature: 'withdrawals', minLevel: 'read' }}>
                <WithdrawalDetailContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='user/investments/:investorId'
            element={
              <PrivateRoute requiredFeature={{ feature: 'investments', minLevel: 'read' }}>
                <InvestmentOpportunityListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='user/withdrawals/:investorId'
            element={
              <PrivateRoute requiredFeature={{ feature: 'withdrawals', minLevel: 'read' }}>
                <WithdrawalOpportunityListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='opportunities'
            element={
              <PrivateRoute requiredFeature={{ feature: 'opportunities', minLevel: 'read' }}>
                <OpportunityListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='inquiries'
            element={
              <PrivateRoute requiredFeature={{ feature: 'inquiries', minLevel: 'read' }}>
                <QueriesList />
              </PrivateRoute>
            }
          />
          <Route
            path='business-inquiries'
            element={
              <PrivateRoute requiredFeature={{ feature: 'businessInquiries', minLevel: 'read' }}>
                <BusinessInquiriesList />
              </PrivateRoute>
            }
          />
          <Route
            path='orders'
            element={
              <PrivateRoute requiredFeature={{ feature: 'orders', minLevel: 'read' }}>
                <OrdersContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='wallet/topups'
            element={
              <PrivateRoute requiredFeature={{ feature: 'investments', minLevel: 'read' }}>
                <WalletTopupsContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='wallet/withdrawals'
            element={
              <PrivateRoute requiredFeature={{ feature: 'withdrawals', minLevel: 'read' }}>
                <WalletWithdrawalsContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='trading'
            element={
              <PrivateRoute requiredFeature={{ feature: 'investments', minLevel: 'read' }}>
                <TradingAdminContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='allocation-requests'
            element={
              <PrivateRoute requiredFeature={{ feature: 'opportunities', minLevel: 'read' }}>
                <AllocationRequestsListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='allocation-requests/:id'
            element={
              <PrivateRoute requiredFeature={{ feature: 'opportunities', minLevel: 'read' }}>
                <AllocationRequestDetailContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='investment-limit-requests'
            element={
              <PrivateRoute requiredFeature={{ feature: 'kyc', minLevel: 'read' }}>
                <InvestmentLimitRequestsListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='investment-limit-requests/:id'
            element={
              <PrivateRoute requiredFeature={{ feature: 'kyc', minLevel: 'read' }}>
                <InvestmentLimitRequestDetailContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='secondary-market'
            element={
              <PrivateRoute requiredFeature={{ feature: 'secondaryMarket', minLevel: 'read' }}>
                <SecondaryMarketOrdersContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='/unit-prices'
            element={
              <PrivateRoute requiredFeature={{ feature: 'unitPrices', minLevel: 'write' }}>
                <UnitPriceFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='interest-payouts'
            element={
              <PrivateRoute requiredFeature={{ feature: 'interestPayouts', minLevel: 'write' }}>
                <InterestPayoutFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='dividend-payouts'
            element={
              <PrivateRoute requiredFeature={{ feature: 'dividendPayouts', minLevel: 'write' }}>
                <DividendPayoutFormContainer />
              </PrivateRoute>
            }
          />

          <Route
            path='coming-soon'
            element={
              <PrivateRoute requiredFeature={{ feature: 'comingSoon', minLevel: 'read' }}>
                <ComingSoonListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='coming-soon/new'
            element={
              <PrivateRoute requiredFeature={{ feature: 'comingSoon', minLevel: 'write' }}>
                <ComingSoonFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='coming-soon/:id/edit'
            element={
              <PrivateRoute requiredFeature={{ feature: 'comingSoon', minLevel: 'write' }}>
                <ComingSoonFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='blogs'
            element={
              <PrivateRoute requiredFeature={{ feature: 'blogs', minLevel: 'read' }}>
                <BlogListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='blogs/new'
            element={
              <PrivateRoute requiredFeature={{ feature: 'blogs', minLevel: 'write' }}>
                <BlogsFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='blogs/:id/edit'
            element={
              <PrivateRoute requiredFeature={{ feature: 'blogs', minLevel: 'write' }}>
                <BlogsFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='webinars'
            element={
              <PrivateRoute requiredFeature={{ feature: 'webinars', minLevel: 'read' }}>
                <WebinarListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='webinars/new'
            element={
              <PrivateRoute requiredFeature={{ feature: 'webinars', minLevel: 'write' }}>
                <WebinarsFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='webinars/:id/edit'
            element={
              <PrivateRoute requiredFeature={{ feature: 'webinars', minLevel: 'write' }}>
                <WebinarsFormContainer />
              </PrivateRoute>
            }
          />

          <Route
            path='faqs'
            element={
              <PrivateRoute requiredFeature={{ feature: 'faqs', minLevel: 'read' }}>
                <FaqListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='faqs/new'
            element={
              <PrivateRoute requiredFeature={{ feature: 'faqs', minLevel: 'write' }}>
                <FaqFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='faqs/:id/edit'
            element={
              <PrivateRoute requiredFeature={{ feature: 'faqs', minLevel: 'write' }}>
                <FaqFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='faqs/opportunity-questions/:questionId/reply'
            element={
              <PrivateRoute requiredFeature={{ feature: 'opportunities', minLevel: 'write' }}>
                <OpportunityQuestionReplyContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='faqs/opportunity-questions'
            element={
              <PrivateRoute requiredFeature={{ feature: 'opportunities', minLevel: 'read' }}>
                <OpportunityQuestionsContainer />
              </PrivateRoute>
            }
          />

          <Route
            path='newsletter/subscribers'
            element={
              <PrivateRoute requiredFeature={{ feature: 'newsletter', minLevel: 'read' }}>
                <NewsletterSubscribersListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='newsletter/groups'
            element={
              <PrivateRoute requiredFeature={{ feature: 'newsletter', minLevel: 'read' }}>
                <NewsletterGroupsListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='newsletter/groups/new'
            element={
              <PrivateRoute requiredFeature={{ feature: 'newsletter', minLevel: 'write' }}>
                <NewsletterGroupFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='newsletter/groups/:groupId/edit'
            element={
              <PrivateRoute requiredFeature={{ feature: 'newsletter', minLevel: 'write' }}>
                <NewsletterGroupFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='newsletter/send'
            element={
              <PrivateRoute requiredFeature={{ feature: 'newsletter', minLevel: 'write' }}>
                <NewsletterSendFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='news'
            element={
              <PrivateRoute requiredFeature={{ feature: 'opportunities', minLevel: 'read' }}>
                <OpportunityNewsListContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='news/schedule'
            element={
              <PrivateRoute requiredFeature={{ feature: 'adminTeam', minLevel: 'write' }}>
                <NewsScheduleFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='admin-team'
            element={
              <PrivateRoute requiredFeature={{ feature: 'adminTeam', minLevel: 'write' }}>
                <AdminTeamContainer />
              </PrivateRoute>
            }
          />

          <Route
            path='campaigns/new'
            element={
              <PrivateRoute requiredFeature={{ feature: 'campaigns', minLevel: 'write' }}>
                <CampaignFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='account/change-password'
            element={
              <PrivateRoute>
                <AccountChangePasswordContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='campaigns/:campaignId/edit'
            element={
              <PrivateRoute requiredFeature={{ feature: 'campaigns', minLevel: 'write' }}>
                <CampaignFormContainer />
              </PrivateRoute>
            }
          />
          <Route
            path='campaigns'
            element={
              <PrivateRoute requiredFeature={{ feature: 'campaigns', minLevel: 'read' }}>
                <CampaignsListContainer />
              </PrivateRoute>
            }
          />
        </Routes>
      </div>
      <Footer />
    </BrowserRouter>
  )
}

export default App
